import type { DocumentationDraft } from "./models";

const sameDraft = (left: DocumentationDraft, right: DocumentationDraft): boolean =>
    left.id === right.id &&
    left.project_id === right.project_id &&
    left.organization_id === right.organization_id &&
    left.documentation_group_id === right.documentation_group_id;

/** A slower group read must not replace a newer authoritative card response. */
export function mergeDocumentationDrafts(current: readonly DocumentationDraft[], incoming: readonly DocumentationDraft[]): DocumentationDraft[] {
    return incoming.map(draft => {
        const existing = current.find(item => sameDraft(item, draft));
        return existing && existing.revision > draft.revision ? existing : draft;
    });
}

/** Replace an existing draft only; a response from an abandoned flow cannot add it back. */
export function applyDocumentationDraft(current: readonly DocumentationDraft[], updated: DocumentationDraft): DocumentationDraft[] {
    return current.map(draft => (sameDraft(draft, updated) && updated.revision >= draft.revision ? updated : draft));
}

type CardActionOptions = {
    write: () => Promise<DocumentationDraft>;
    recover: () => Promise<DocumentationDraft>;
    apply: (draft: DocumentationDraft) => void;
    isCurrent: () => boolean;
    setBusy: (busy: boolean) => void;
};

/** Success needs one request. A failed write is reconciled by a read, never repeated. */
export async function runDocumentationCardAction({ write, recover, apply, isCurrent, setBusy }: CardActionOptions): Promise<void> {
    if (!isCurrent()) return;
    setBusy(true);
    try {
        const updated = await write();
        if (isCurrent()) apply(updated);
    } catch (actionError: unknown) {
        if (isCurrent()) {
            try {
                const latest = await recover();
                if (isCurrent()) apply(latest);
            } catch {
                // Keep the original action error, including when its response was lost.
            }
        }
        throw actionError;
    } finally {
        if (isCurrent()) setBusy(false);
    }
}
